import React, { useEffect } from 'react';
import { Dialog, Grid, Typography } from '@material-ui/core';
import { mainContainer, gridItem, icon, button, emailIcon, inlineBlock } from './Prompt.styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { ButtonOne } from '../ButtonOne/ButtonOne.component';
import LoginIcon from '@mui/icons-material/Login';
import { useTranslation } from 'react-i18next';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { usePromptContext } from '../../Context/Prompt.context';

export interface SubText {
  text?: React.ReactNode;
  isBold?: boolean;
  fontSize?: number;
  inlineLink?: JSX.Element;
}

export type promptSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;

export type promptType =
  | 'confirmation'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'confirm email'
  | 'link already used'
  | 'expired link'
  | 'login'
  | 'unread message';

export interface PromptProps {
  type: promptType;
  open?: boolean;
  mainText?: React.ReactNode;
  subTexts?: SubText[];
  subIconText?: string;
  enhancedText?: JSX.Element;
  mainActionButtonText?: string;
  cancelButtonText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  isBold?: boolean;
  subTexts2?: SubText[];
  size?: promptSize;
  isRemovedBackdrop?: boolean;
  withHorizontalLineUnderMainText?: boolean;
}

export const Prompt: React.FC<PromptProps> = (props) => {
  const { t } = useTranslation();
  const { setForceClosingAlert } = usePromptContext();

  const iconSelector = (): JSX.Element => {
    switch (props.type) {
      case 'error':
        return <HighlightOffIcon htmlColor="#BC0000" style={icon} />;
      case 'confirmation':
        return <HelpOutlineIcon htmlColor="#FF9A02" style={icon} />;
      case 'success':
        return <CheckCircleOutlineIcon htmlColor="#347F42" style={icon} />;
      case 'warning':
        return <WarningAmberIcon htmlColor="#BC0000" style={icon} />;
      case 'confirm email':
        return <MarkEmailReadOutlinedIcon htmlColor="#C0C2EC" style={emailIcon} />;
      case 'expired link':
        return <WarningAmberIcon htmlColor="#BC0000" style={emailIcon} />;
      case 'link already used':
        return <WarningAmberIcon htmlColor="#BC0000" style={emailIcon} />;
      case 'info':
        return <InfoOutlinedIcon htmlColor="#3560FF" style={icon} />;
      case 'login':
        return <LoginIcon htmlColor="#C0C2EC" style={emailIcon} />;
      case 'unread message':
        return <MailOutlineIcon htmlColor="#3C419A" style={icon} />;
      default:
        return <InfoOutlinedIcon style={icon} />;
    }
  };

  const isBold = props.isBold ?? props.isBold;
  const gridItemWithInlineBlock = { ...gridItem, ...inlineBlock };

  useEffect(() => {
    if (props.open) {
      setForceClosingAlert(true);
    }
  }, [props.open]);

  return (
    <Dialog
      aria-labelledby={props.mainText ? "modal-title" : undefined}
      open={props.open !== undefined ? props.open : true}
      onClose={props.onCancel ?? props.onConfirm}
      maxWidth={props.size}
      BackdropProps={{ style: { display: props.isRemovedBackdrop ? 'none' : undefined } }}
    >
      <Grid container justifyContent="center" alignItems="center" style={mainContainer}>
        <Grid item xs={12} style={gridItem}>
          {iconSelector()}
        </Grid>
        {props.subIconText && (
          <Grid item xs={12} style={gridItem}>
            <Typography
              variant="subtitle2"
              style={{
                fontWeight: 800,
                color: '#3C419A',
              }}
            >
              {props.subIconText}
            </Typography>
          </Grid>
        )}
        {props.mainText && (
          <Grid item xs={12} style={gridItem}>
            <Typography
              id = "modal-title"
              variant="h2"
              style={{
                fontWeight: isBold ? 800 : 400,
                fontSize: "1.25rem"
              }}
            >
              {props.mainText}
            </Typography>
          </Grid>
        )}

        {props.withHorizontalLineUnderMainText && (
          <Grid item xs={12}>
            <div style={{ borderBottom: 'solid 1px #2C2D3E4D', margin: '15px 0 10px 0' }} />
          </Grid>
        )}
        {props.subTexts && (
          <div
            style={{
              maxHeight: '250px',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            {props.subTexts.map((subText: SubText, index) => (
              <React.Fragment
                key={subText && typeof subText.text === 'string' ? `${subText.text}-${index}` : `subtext ${index}`}
              >
                {subText.text && (
                  <Grid item xs={11} style={gridItemWithInlineBlock} key={index + '_' + subText}>
                    <Typography
                      variant="body1"
                      style={{
                        fontWeight: subText.isBold ? 800 : 400,
                        fontSize: subText.fontSize ? subText.fontSize : 15,
                      }}
                    >
                      {subText.text}
                    </Typography>
                  </Grid>
                )}
                {subText.inlineLink && (
                  <Typography
                    variant="body1"
                    style={{
                      fontWeight: subText.isBold ? 800 : 400,
                      fontSize: subText.fontSize ? subText.fontSize : 15,
                      marginRight: '0.4em',
                    }}
                  >
                    {subText.inlineLink}
                    {props.subTexts && props.subTexts[index + 1] && props.subTexts[index + 1].inlineLink && ','}
                  </Typography>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
        <Grid item xs={11} style={gridItem} key={'enhancedText'}>
          {props.enhancedText}
        </Grid>
        {props.children && (
          <Grid item xs={11} style={gridItem} key={'children'}>
            {props.children}
          </Grid>
        )}
        {props.subTexts2 &&
          props.subTexts2.map((subText2, index) => (
            <Grid item xs={11} style={gridItem} key={index + '_' + subText2}>
              <Typography
                variant="body1"
                component="div"
                style={{
                  fontWeight: subText2.isBold ? 800 : 400,
                  fontSize: subText2.fontSize ? subText2.fontSize : 15,
                }}
              >
                {subText2.text ? subText2.text : ''}
              </Typography>
            </Grid>
          ))}
        <Grid xs={12} item style={{ textAlign: 'center' }}>
          {props.onConfirm && (
            <Grid item xs={12} style={gridItem}>
              {props.onCancel && (
                <ButtonOne
                  variant="outlined"
                  color="primary"
                  style={button}
                  onClick={props.onCancel}
                  data-testid="onCancelButton"
                >
                  {props.cancelButtonText ? props.cancelButtonText : 'Anuluj'}
                </ButtonOne>
              )}
              <ButtonOne variant="contained" color="primary" style={button} onClick={props.onConfirm} autoFocus>
                {props.mainActionButtonText ? props.mainActionButtonText : 'OK'}
              </ButtonOne>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Dialog>
  );
};
